import type Guid from "_shared/guids/guid";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import type Permission from "permissions/permission";
import type { Resource } from "permissions/resources/permission-resource";

/**
 * What applying a template does to one resource for one grantee.
 *
 * The three kinds mirror what the server actually does for
 * `POST permissions/bulk` with `replaceMatchingResourceTypes` (see
 * `PermissionManagementService.AddPermissionsAsync`): every resource the
 * template enables is granted, every resource it disables is revoked, and any
 * other active permission of a kind the template names is disabled so the
 * grantee ends up matching the template. A kind is a resource type plus, for a
 * type that is several kinds, its discriminator: Signing Authority and Member
 * Permissions are two kinds of `LegalEntityAccess`, and a template naming one
 * leaves the other alone.
 */
export enum TemplateChangeKind {
  Granted = "Granted",
  Revoked = "Revoked",
  Unchanged = "Unchanged",
}

export type TemplateResourceChange = {
  kind: TemplateChangeKind;
  /** The resource whose access changes — from the template, or from the permission being replaced. */
  resource: Resource;
  /** The permission the change acts on: the one that is kept, or the one that will be disabled. */
  existingPermission?: Permission;
};

export type GranteeTemplateChanges = {
  granteeUserId: Guid;
  /** Granted first, then revoked, then unchanged; each group ordered by resource type. */
  changes: TemplateResourceChange[];
  grantedCount: number;
  revokedCount: number;
  unchangedCount: number;
};

export type TemplateApplicationPlan = {
  grantees: GranteeTemplateChanges[];
  grantedCount: number;
  revokedCount: number;
  unchangedCount: number;
  /** False when applying the template would leave every grantee exactly as they are. */
  hasChanges: boolean;
};

export type TemplateApplicationPlanParams = {
  /** The resources that will be sent, after any edits made before applying. */
  resources: Resource[];
  granteeUserIds: Guid[];
  /** The grantees' current permissions. Disabled ones are ignored, as the server ignores them. */
  existingPermissions: Permission[];
  /**
   * The kinds table (`GET permissions/kinds`), which says which resources are one kind. Without it,
   * every resource of a type counts as one kind: the preview then lists more revocations than the
   * server makes, never fewer.
   */
  kinds?: readonly PermissionKind[];
};

/**
 * The server strips these before persisting a resource and compares what is left
 * (`NormalizeResourceForPersistence` plus `JsonElement.DeepEquals`), so a signature
 * that ignores them matches a template resource to the permission it would reuse.
 */
const UNPERSISTED_RESOURCE_FIELDS: ReadonlySet<string> = new Set(["isDisabled", "resourceId"]);

const KIND_ORDER: Record<TemplateChangeKind, number> = {
  [TemplateChangeKind.Granted]: 0,
  [TemplateChangeKind.Revoked]: 1,
  [TemplateChangeKind.Unchanged]: 2,
};

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value === null || typeof value !== "object") return value;

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter(([, fieldValue]) => fieldValue !== null && fieldValue !== undefined)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, fieldValue]) => [key, canonicalize(fieldValue)]),
  );
}

/**
 * A stable key for the resource as the server stores it. Two resources share a
 * signature exactly when the server would treat a grant of one as already
 * satisfied by a permission holding the other.
 */
export function resourceSignature(resource: Resource): string {
  let json: Record<string, unknown>;
  try {
    json = resource.toJSON() as Record<string, unknown>;
  } catch {
    // A resource the schema rejects still needs a key that separates it from its
    // neighbours; its own fields are the closest stand-in for the persisted shape.
    json = { ...(resource as unknown as Record<string, unknown>) };
  }

  const persistedFields = Object.entries(json).filter(([key]) => !UNPERSISTED_RESOURCE_FIELDS.has(key));
  return JSON.stringify(canonicalize(Object.fromEntries(persistedFields)));
}

/** Drops resources the server would discard as duplicates, keeping the first of each signature. */
function dedupeBySignature(resources: Resource[]): Resource[] {
  const seen = new Set<string>();
  return resources.filter((resource) => {
    const signature = resourceSignature(resource);
    if (seen.has(signature)) return false;
    seen.add(signature);
    return true;
  });
}

function groupActivePermissionsByGrantee(permissions: Permission[]): Map<string, Permission[]> {
  const byGrantee = new Map<string, Permission[]>();
  for (const permission of permissions) {
    if (permission.isDisabled) continue;
    const key = permission.granteeUserId.value;
    const granteePermissions = byGrantee.get(key);
    if (granteePermissions) granteePermissions.push(permission);
    else byGrantee.set(key, [permission]);
  }
  return byGrantee;
}

/**
 * The kind a resource is, keyed as the server keys its replacement (`KindSlugOf`, after
 * `PermissionAreas.Find`): a resource the table does not name is one kind with every other such
 * resource of its type.
 */
export function replacementKey(kinds: readonly PermissionKind[], resource: Resource): string {
  const properties = resource as unknown as Record<string, unknown>;
  const kind = kinds.find(
    (candidate) =>
      candidate.resourceType === resource.resourceType &&
      (candidate.discriminator === null || properties[candidate.discriminator.property] === candidate.discriminator.value),
  );
  return `${resource.resourceType}/${kind?.slug ?? ""}`;
}

function compareChanges(left: TemplateResourceChange, right: TemplateResourceChange): number {
  const kindDifference = KIND_ORDER[left.kind] - KIND_ORDER[right.kind];
  if (kindDifference !== 0) return kindDifference;
  return left.resource.resourceType.localeCompare(right.resource.resourceType);
}

function buildGranteeChanges(
  granteeUserId: Guid,
  resources: Resource[],
  templateKinds: ReadonlySet<string>,
  kinds: readonly PermissionKind[],
  activePermissions: Permission[],
): GranteeTemplateChanges {
  const permissionBySignature = new Map<string, Permission>();
  for (const permission of activePermissions) {
    const signature = resourceSignature(permission.resource);
    if (!permissionBySignature.has(signature)) permissionBySignature.set(signature, permission);
  }

  const changes: TemplateResourceChange[] = [];
  const appliedSignatures = new Set<string>();

  for (const resource of resources) {
    const signature = resourceSignature(resource);
    appliedSignatures.add(signature);
    const existingPermission = permissionBySignature.get(signature);

    if (resource.isDisabled) {
      // Nothing to report when the template turns off access the grantee never had:
      // the server creates that permission and disables it in the same breath.
      if (existingPermission) {
        changes.push({ kind: TemplateChangeKind.Revoked, resource, existingPermission });
      }
      continue;
    }

    changes.push(
      existingPermission
        ? { kind: TemplateChangeKind.Unchanged, resource, existingPermission }
        : { kind: TemplateChangeKind.Granted, resource },
    );
  }

  // Whatever the template does not name but shares a kind with is swept away by
  // `replaceMatchingResourceTypes`. Those revocations are the ones people miss,
  // which is the whole reason this preview exists.
  for (const permission of activePermissions) {
    if (!templateKinds.has(replacementKey(kinds, permission.resource))) continue;
    if (appliedSignatures.has(resourceSignature(permission.resource))) continue;

    changes.push({
      kind: TemplateChangeKind.Revoked,
      resource: permission.resource,
      existingPermission: permission,
    });
  }

  changes.sort(compareChanges);

  return {
    granteeUserId,
    changes,
    grantedCount: changes.filter((change) => change.kind === TemplateChangeKind.Granted).length,
    revokedCount: changes.filter((change) => change.kind === TemplateChangeKind.Revoked).length,
    unchangedCount: changes.filter((change) => change.kind === TemplateChangeKind.Unchanged).length,
  };
}

/** Works out, per grantee, what applying `resources` would add, take away, and leave alone. */
export function buildTemplateApplicationPlan(params: TemplateApplicationPlanParams): TemplateApplicationPlan {
  const resources = dedupeBySignature(params.resources);
  const kinds = params.kinds ?? [];
  const templateKinds = new Set<string>(resources.map((resource) => replacementKey(kinds, resource)));
  const activePermissionsByGrantee = groupActivePermissionsByGrantee(params.existingPermissions);

  const grantees = params.granteeUserIds.map((granteeUserId) =>
    buildGranteeChanges(
      granteeUserId,
      resources,
      templateKinds,
      kinds,
      activePermissionsByGrantee.get(granteeUserId.value) ?? [],
    ),
  );

  const grantedCount = grantees.reduce((total, grantee) => total + grantee.grantedCount, 0);
  const revokedCount = grantees.reduce((total, grantee) => total + grantee.revokedCount, 0);
  const unchangedCount = grantees.reduce((total, grantee) => total + grantee.unchangedCount, 0);

  return {
    grantees,
    grantedCount,
    revokedCount,
    unchangedCount,
    hasChanges: grantedCount > 0 || revokedCount > 0,
  };
}
